import {
  type PropsWithChildren,
  useMemo,
  createContext,
  useContext,
  forwardRef,
} from 'react'
import { useLocation } from 'react-router'
import { ProductRouteType } from 'app/utils/productPages/types'
import {
  BUSINESS_PAGE_URL,
  DISCOVERY_URL,
  GIFT_CARD,
  PHOTO_BOOK_URL,
  PHOTO_STYLER_URL,
  PHOTO_WALL_URL,
  REFERRAL,
} from '../config/config'
import { PRODUCT_TYPES, productTypeState } from './ProductTypeState'

type ProductTypeContextType = {
  productType: PRODUCT_TYPES
}

const ProductTypeContext = createContext<ProductTypeContextType | undefined>(
  undefined
)

export default function ProductTypeProvider({ children }: PropsWithChildren) {
  const { pathname } = useLocation()
  const productType = useMemo<PRODUCT_TYPES>(() => {
    const newProductType = getProductType(pathname) || PRODUCT_TYPES.CLASSIC
    productTypeState.setProductType(newProductType)
    return newProductType
  }, [pathname])

  return (
    <ProductTypeContext.Provider value={{ productType }}>
      {children}
    </ProductTypeContext.Provider>
  )
}

export function useProductType() {
  const context = useContext(ProductTypeContext)
  if (!context) {
    throw new Error('useProductType must be used within a ProductTypeProvider')
  }

  return context
}

export function withProductType(Component: any) {
  return forwardRef((props, ref) => (
    <ProductTypeContext.Consumer>
      {(context) => <Component {...props} ref={ref} {...context} />}
    </ProductTypeContext.Consumer>
  ))
}

export function getProductType(pathname: string) {
  if (
    pathname === '/' ||
    pathname.startsWith(PHOTO_STYLER_URL) ||
    pathname.startsWith(DISCOVERY_URL) ||
    pathname.startsWith(PHOTO_WALL_URL)
  ) {
    return PRODUCT_TYPES.CLASSIC
  }

  if (pathname.startsWith(GIFT_CARD) || pathname.startsWith(REFERRAL)) {
    return PRODUCT_TYPES.GIFT_CARD
  }
  if (pathname.startsWith(BUSINESS_PAGE_URL)) {
    return PRODUCT_TYPES.BUSINESS
  }
  if (
    pathname.startsWith(PHOTO_BOOK_URL) ||
    pathname.includes(ProductRouteType.PHOTOBOOKS)
  ) {
    return PRODUCT_TYPES.PHOTO_BOOK
  }
  return null
}
